import React, { useEffect, useState } from 'react'
import { EmptyContainer } from './styles'
import { MotherTimelineView } from '../MotherTimelineView'
import { BabyTimelineView } from '../BabyTimelineView'
import { useMaternalInfantHealthProcedures, usePatient } from '~/context'
import { getIndexSelectedBaby, IHealthProcedures, IPatient } from '~/interfaces'
import { Loader, NotFound } from '~/components/shared/medicalViews'
import { SelectPregnancyMessage } from '~/components/maternalInfantMedicalView'
import { useSelectedMedicalView } from '~/hooks'
import { getTimestamp, hasObjectValue } from '~/utils'

import {
  babyGestationDevelopmentStageLabel,
  motherGestationDevelopmentStageLabel
} from '~/utils/dashboard/timeline/maternalInfant'
import { CreateHealthProcedureDetails } from '~/components/healthProcedures/CreateHealthProcedureDetails'

export const Content = (): JSX.Element => {
  const { selectedMedicalView } = useSelectedMedicalView()
  const { patient, setNewPatientTag } = usePatient()
  const maternalInfantHealthProcedures = useMaternalInfantHealthProcedures()

  const {
    sidePanelEvents,
    isLoadingTimeline,
    modalHealthProcedure,
    selectedPregnancy,
    selectedBaby,
    isMotherTimelineSelected,
    isBabyTimelineSelected,
    handleSelectedModalHealthProcedures,
    getBabyHealthProcedures,
    getMotherHealthProcedures
  } = maternalInfantHealthProcedures

  const [childPatient, setChildPatient] = useState<IPatient>({} as IPatient)

  useEffect(() => {
    const hasSelectedPregnancy = hasObjectValue({ ...selectedPregnancy })
    if (hasSelectedPregnancy && isMotherTimelineSelected) {
      const queryParams = selectedPregnancy.pregnancyId ? { pregnancyId: String(selectedPregnancy.pregnancyId) } : {}
      getMotherHealthProcedures({ pathParams: { patientId: patient.id }, queryParams })
      const patientTag = motherGestationDevelopmentStageLabel(selectedPregnancy)
      setNewPatientTag(patientTag)

      const selectedPregnancyHasSomeBaby = selectedPregnancy?.children.length > 0
      if (selectedPregnancyHasSomeBaby) {
        setChildPatient({ ...selectedPregnancy.children.filter(c => c.id === selectedBaby)[0] } as IPatient)
      }
    }
  }, [getMotherHealthProcedures, selectedPregnancy, selectedMedicalView, isMotherTimelineSelected])

  useEffect(() => {
    if (isBabyTimelineSelected) {
      const queryParams = selectedPregnancy.pregnancyId ? { pregnancyId: String(selectedPregnancy.pregnancyId) } : {}

      getBabyHealthProcedures({ pathParams: { patientId: selectedBaby ?? 0 }, queryParams })
      const index = getIndexSelectedBaby(selectedPregnancy, selectedBaby)
      const babyStage = babyGestationDevelopmentStageLabel(selectedPregnancy)
      const patientTag = selectedPregnancy.children.length > 1 ? `${babyStage} ${index}` : babyStage
      setNewPatientTag(patientTag)

      setChildPatient({ ...selectedPregnancy.children.filter(c => c.id === selectedBaby)[0] } as IPatient)
    }
  }, [getBabyHealthProcedures, selectedBaby, selectedMedicalView, isBabyTimelineSelected])

  const hasData = (): boolean => {
    return sidePanelEvents && sidePanelEvents.length > 0
  }

  const isFirstFetchOfHealthProcedures = (): boolean => {
    return isLoadingTimeline && !hasData()
  }

  const getPatientIdOfHealthProcedure = (
    patient: IPatient,
    selectedHealthProcedure: IHealthProcedures | null
  ): number => {
    if (isMotherTimelineSelected || !selectedHealthProcedure) return patient.id

    const isHealthProcedureInGestationPeriod =
      getTimestamp(selectedHealthProcedure.startDate) <= getTimestamp(selectedPregnancy.endDate)

    if (isHealthProcedureInGestationPeriod) return patient.id

    return selectedBaby as number
  }

  const renderDashboard = () => {
    if (!hasObjectValue({ ...selectedPregnancy }))
      return (
        <EmptyContainer>
          <SelectPregnancyMessage />
        </EmptyContainer>
      )

    if (isFirstFetchOfHealthProcedures())
      return (
        <EmptyContainer>
          <Loader />
        </EmptyContainer>
      )

    if (hasData() && isBabyTimelineSelected) {
      return <BabyTimelineView motherPatient={patient} childPatient={childPatient} />
    }

    if (hasData() && isMotherTimelineSelected) {
      return <MotherTimelineView />
    }

    return (
      <EmptyContainer>
        <NotFound />
      </EmptyContainer>
    )
  }

  return (
    <>
      {renderDashboard()}

      <CreateHealthProcedureDetails
        handleSelectedModalHealthProcedures={() => handleSelectedModalHealthProcedures(null)}
        selectedPatient={() => getPatientIdOfHealthProcedure(patient, modalHealthProcedure)}
        modalHealthProcedure={modalHealthProcedure}
      />
    </>
  )
}
